/**
 * Core: Canvas imports
 * -----------------------------------------------------------------------------
 * Import async components, global components, and Vuex stores for use in
 * CanvasApp Vue instance.
 *
 */
/* eslint-disable max-len, no-inline-comments, quote-props */
import { defineAsyncComponent } from 'vue'

/**
 * Get async components and imports.
 * - Must be manually updated with new async components (components/async/).
 * - Previously used a dynamic import statement which caused infinite build
 *   issue and generated unused files.
 * - Only parent components should be imported.
 * - Do not import components that are only ever used as a child component.
 * - Do not import async sub-components.
 * @returns {Object}
 */
export function getAsyncComponents() {
  return {
    // canvas-async-object-start
    'accordion-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.accordion-section' */'~async/accordion-section/accordion-section') }),
    'article-footer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.article-footer' */'~async/article-footer/article-footer') }),
    'back-in-stock': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.back-in-stock' */'~async/back-in-stock/back-in-stock') }),
    'blog-carousel': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.blog-carousel' */'~async/blog-carousel/blog-carousel') }),
    'nosto-range-slider': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.nosto-range-slider' */'~async/nosto-range-slider/nosto-range-slider') }),
    'brand-directory': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.brand-directory' */'~async/brand-directory/brand-directory') }),
    'brand-ticker': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.brand-ticker' */'~async/brand-ticker/brand-ticker') }),
    'cart-drawer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.cart-drawer' */'~async/cart-drawer/cart-drawer') }),
    'complete-the-look': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.complete-the-look' */'~async/complete-the-look/complete-the-look') }),
    'countdown-timer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.countdown-timer' */'~async/countdown-timer/countdown-timer') }),
    'customers-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-account' */'~async/customers-account/customers-account') }),
    'customers-activate-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-activate-account' */'~async/customers-activate-account/customers-activate-account') }),
    'customers-addresses': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-addresses' */'~async/customers-addresses/customers-addresses') }),
    'customers-login': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-login' */'~async/customers-login/customers-login') }),
    'customers-order': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-order' */'~async/customers-order/customers-order') }),
    'customers-orders': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-orders' */'~async/customers-orders/customers-orders') }),
    'customers-register': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-register' */'~async/customers-register/customers-register') }),
    'customers-reset-password': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-reset-password' */'~async/customers-reset-password/customers-reset-password') }),
    'dual-blocks': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.dual-blocks' */'~async/dual-blocks/dual-blocks') }),
    'dynamic-accordion': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.dynamic-accordion' */'~async/dynamic-accordion/dynamic-accordion') }),
    'gateway-blocks': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.gateway-blocks' */'~async/gateway-blocks/gateway-blocks') }),
    'gift-wrap-item': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.gift-wrap-item' */'~async/gift-wrap-item/gift-wrap-item') }),
    'hero-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.hero-banner' */'~async/hero-banner/hero-banner') }),
    'image-and-text': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.image-and-text' */'~async/image-and-text/image-and-text') }),
    'main-article': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-article' */'~async/main-article/main-article') }),
    'main-blog': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-blog' */'~async/main-blog/main-blog') }),
    'main-cart': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-cart' */'~async/main-cart/main-cart') }),
    'main-collection': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-collection' */'~async/main-collection/main-collection') }),
    'main-list-collections': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-list-collections' */'~async/main-list-collections/main-list-collections') }),
    'main-product': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-product' */'~async/main-product/main-product') }),
    'main-search': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-search' */'~async/main-search/main-search') }),
    'multi-store-modal': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.multi-store-modal' */'~async/multi-store-modal/multi-store-modal') }),
    'multi-store-options': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.multi-store-options' */'~async/multi-store-options/multi-store-options') }),
    'newsletter-form': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.newsletter-form' */'~async/newsletter-form/newsletter-form') }),
    'next-day-shipping-data': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.next-day-shipping-data' */'~async/next-day-shipping-data/next-day-shipping-data') }),
    'page-contact': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.page-contact' */'~async/page-contact/page-contact') }),
    'page-size-guide': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.page-size-guide' */'~async/page-size-guide/page-size-guide') }),
    'physical-links-button': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.physical-links-button' */'~async/physical-links-button/physical-links-button') }),
    'pills-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.pills-section' */'~async/pills-section/pills-section') }),
    'predictive-search': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.predictive-search' */'~async/predictive-search/predictive-search') }),
    'product-recommendations': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-recommendations' */'~async/product-recommendations/product-recommendations') }),
    'quick-view': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.quick-view' */'~async/quick-view/quick-view') }),
    'signup-form': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.signup-form' */'~async/signup-form/signup-form') }),
    'site-footer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.site-footer' */'~async/site-footer/site-footer') }),
    'size-guide-tables': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.size-guide-tables' */'~async/size-guide-tables/size-guide-tables') }),
    'stylitics-recommendations': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.stylitics-recommendations' */'~async/stylitics-recommendations/stylitics-recommendations') }),
    'text-block': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.text-block' */'~async/text-block/text-block') }),
    'video-player': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.video-player' */'~async/video-player/video-player') }),
    'video-player-toggle': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.video-player-toggle' */'~async/video-player-toggle/video-player-toggle') }),
    'wishlist-button': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.wishlist-button' */'~async/wishlist-button/wishlist-button') }),
    // 'cnvs-featured-products': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.cnvs-featured-products' */'~async/cnvs-featured-products/cnvs-featured-products') }),
    // canvas-async-object-end
  }
}

/**
 * Import global components here.
 */
// canvas-global-import-start
import Accessibility from '~global/core/accessibility'
import ArtDirection from '~global/images/art-direction'
import NostoApiCall from '~global/nosto-api-call/nosto-api-call'
import Btn from '~global/btn/btn'
import Index from '~global/core/index'
import MultiStore from '~global/multi-store/multi-store'
import Nosto from '~global/nosto/nosto'
import Overlay from '~global/overlay/overlay'
import PhysicalLinks from '~global/physical-links/physical-links'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import SiteHeader from '~global/site-header/site-header'
import TransitionHeight from '~global/transition-height/transition-height'
// canvas-global-import-end

/**
 * Get global components and imports.
 * - Must be manually updated with new global components (components/global).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 * @returns {Object}
 */
export function getGlobalComponents() {
  return {
    // canvas-global-object-start
    'accessibility': Accessibility,
    'art-direction': ArtDirection,
    'nosto-api-call': NostoApiCall,
    'btn': Btn,
    'index': Index,
    'multi-store': MultiStore,
    'nosto': Nosto,
    'overlay': Overlay,
    'physical-links': PhysicalLinks,
    'product-prices': ProductPrices,
    'responsive-image': ResponsiveImage,
    'site-header': SiteHeader,
    'transition-height': TransitionHeight,
    // canvas-global-object-end
  }
}

/**
 * Import Vuex stores here.
 * - Import with Store suffix to avoid conflicts with global component imports.
 */
// canvas-stores-import-start
import AccessibilityStore from '~stores/accessibility'
import BackInStockStore from '~stores/back-in-stock'
import CartStore from '~stores/cart'
import ComponentsStore from '~stores/components'
import IndexStore from '~stores/index'
import MainCollectionStore from '~stores/main-collection'
import MultiStoreStore from '~stores/multi-store'
import NextDayShippingStore from '~stores/next-day-shipping'
import NostoStore from '~stores/nosto'
import OverlaysStore from '~stores/overlays'
import QuickViewStore from '~stores/quick-view'
// canvas-stores-import-end

/**
 * Get Vuex stores and imports.
 * - Must be manually updated with new Vuex stores (stores/).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 */
export function getVuexStores() {
  return {
    // canvas-stores-object-start
    'accessibility': AccessibilityStore,
    'back-in-stock': BackInStockStore,
    'cart': CartStore,
    'components': ComponentsStore,
    'index': IndexStore,
    'main-collection': MainCollectionStore,
    'multi-store': MultiStoreStore,
    'next-day-shipping': NextDayShippingStore,
    'nosto': NostoStore,
    'overlays': OverlaysStore,
    'quick-view': QuickViewStore,
    // canvas-stores-object-end
  }
}
